import React, { useEffect, useState, useMemo } from "react";
import Header from "@/components/Header";
import { api, formatINR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import ChatDrawer from "@/components/ChatDrawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, Share2, Send, Wallet, TrendingUp, Users, IndianRupee, Landmark, Plus, Award, Gift, Trophy, Upload, QrCode, FileDown, Sparkles } from "lucide-react";

const STATUS_META = {
  pending: { label: "Pending", cls: "bg-white/10 text-white border-white/10" },
  contacted: { label: "Contacted", cls: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20" },
  test_ride: { label: "Test Ride", cls: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  purchased: { label: "Purchased", cls: "bg-[#00FF66]/10 text-[#00FF66] border-[#00FF66]/30" },
  rejected: { label: "Rejected", cls: "bg-red-500/10 text-red-400 border-red-500/20" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [summary, setSummary] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [models, setModels] = useState([]);
  const [bank, setBank] = useState({ account_holder: "", account_number: "", ifsc: "", upi_id: "" });
  const [newRef, setNewRef] = useState({ referee_name: "", referee_phone: "", city: "", interested_model: "", notes: "" });
  const [payoutAmt, setPayoutAmt] = useState("");
  const [refDialogOpen, setRefDialogOpen] = useState(false);
  const [loyalty, setLoyalty] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [contest, setContest] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [posterOpen, setPosterOpen] = useState(false);
  const [posterLang, setPosterLang] = useState(lang);
  const [voucher, setVoucher] = useState(null);
  const [payoutReminderDismissed, setPayoutReminderDismissed] = useState(
    () => sessionStorage.getItem("payout_reminder_dismissed") === "1"
  );

  const referralLink = useMemo(() => {
    if (!user?.referral_code) return "";
    return `${window.location.origin}/register?ref=${user.referral_code}`;
  }, [user]);

  const load = async () => {
    const [s, r, p, b, m, ly, rd, ct, vc] = await Promise.all([
      api.get("/referrals/summary"),
      api.get("/referrals/mine"),
      api.get("/payouts/mine"),
      api.get("/bank"),
      api.get("/models"),
      api.get("/loyalty"),
      api.get("/loyalty/redemptions"),
      api.get("/contest/my-rank"),
      api.get("/vouchers/mine"),
    ]);
    setSummary(s.data);
    setReferrals(r.data);
    setPayouts(p.data);
    if (b.data && b.data.account_number) setBank(b.data);
    setModels(m.data);
    setLoyalty(ly.data);
    setRedemptions(rd.data);
    setContest(ct.data);
    setVoucher(vc.data);
  };

  useEffect(() => { load(); }, []);

  const copy = (text, label = "Copied") => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  const submitReferral = async (e) => {
    e.preventDefault();
    try {
      await api.post("/referrals", newRef);
      toast.success("Referral submitted!");
      setNewRef({ referee_name: "", referee_phone: "", city: "", interested_model: "", notes: "" });
      setRefDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveBank = async () => {
    try {
      await api.put("/bank", bank);
      toast.success("Bank details saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const requestPayout = async () => {
    const amt = parseFloat(payoutAmt);
    if (!amt || amt <= 0) return toast.error("Enter valid amount");
    try {
      await api.post("/payouts", { amount: amt });
      toast.success("Payout requested");
      setPayoutAmt("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const redeem = async (rewardId) => {
    try {
      await api.post("/loyalty/redeem", { reward_id: rewardId });
      toast.success("Redemption requested! We'll confirm shortly.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const uploadCsv = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/referrals/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Imported ${data.inserted} referrals`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    }
  };

  const posterUrl = user?.referral_code
    ? `${process.env.REACT_APP_BACKEND_URL}/api/poster/${user.referral_code}.png?lang=${posterLang}`
    : "";

  const downloadPoster = async () => {
    try {
      const res = await fetch(posterUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rosen-poster-${user.referral_code}-${posterLang}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Poster downloaded");
    } catch {
      toast.error("Could not download poster");
    }
  };

  if (!summary) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Header />
        <div className="max-w-7xl mx-auto p-10 text-white/50">Loading dashboard…</div>
      </div>
    );
  }

  const shareWA = () => {
    const text = `Get your electric ride from Shri Shanteshwar Enterprises! Use my referral code ${user.referral_code} · ${referralLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise">
      <Header />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {/* header row */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold mb-2">Referrer Dashboard</div>
            <h1 className="font-display font-black text-3xl lg:text-4xl">Hey {user?.name?.split(" ")[0]} 👋</h1>
            <div className="text-white/50 mt-1 text-sm">Role: <span className="text-white/80 uppercase tracking-wider">{user?.role}</span></div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Button onClick={() => { setImportResult(null); setImportOpen(true); }} variant="outline" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white h-11 px-4" data-testid="import-csv-btn">
              <Upload className="w-4 h-4 mr-2" /> Import CSV
            </Button>
            <Button onClick={() => setPosterOpen(true)} variant="outline" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white h-11 px-4" data-testid="qr-poster-btn">
              <QrCode className="w-4 h-4 mr-2" /> QR Poster
            </Button>
            <Dialog open={refDialogOpen} onOpenChange={setRefDialogOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold px-6 h-11" data-testid="new-referral-btn">
                  <Plus className="w-4 h-4 mr-2" /> Submit new referral
                </Button>
              </DialogTrigger>
            <DialogContent className="bg-[#111] border-white/10 text-white sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">New Referral</DialogTitle>
              </DialogHeader>
              <form onSubmit={submitReferral} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-white/60 uppercase tracking-[0.2em]">Referee Name</Label>
                    <Input required value={newRef.referee_name} onChange={(e) => setNewRef({ ...newRef, referee_name: e.target.value })} className="mt-2 bg-black/40 border-white/10" data-testid="ref-name" />
                  </div>
                  <div>
                    <Label className="text-xs text-white/60 uppercase tracking-[0.2em]">Phone</Label>
                    <Input required value={newRef.referee_phone} onChange={(e) => setNewRef({ ...newRef, referee_phone: e.target.value })} className="mt-2 bg-black/40 border-white/10" data-testid="ref-phone" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-white/60 uppercase tracking-[0.2em]">City</Label>
                  <Input required value={newRef.city} onChange={(e) => setNewRef({ ...newRef, city: e.target.value })} className="mt-2 bg-black/40 border-white/10" data-testid="ref-city" />
                </div>
                <div>
                  <Label className="text-xs text-white/60 uppercase tracking-[0.2em]">Interested Model</Label>
                  <Select value={newRef.interested_model} onValueChange={(v) => setNewRef({ ...newRef, interested_model: v })}>
                    <SelectTrigger className="mt-2 bg-black/40 border-white/10" data-testid="ref-model">
                      <SelectValue placeholder="Choose a model" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10 text-white">
                      {models.map((m) => (
                        <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-white/60 uppercase tracking-[0.2em]">Notes</Label>
                  <Textarea value={newRef.notes} onChange={(e) => setNewRef({ ...newRef, notes: e.target.value })} className="mt-2 bg-black/40 border-white/10" data-testid="ref-notes" />
                </div>
                <DialogFooter>
                  <Button type="submit" className="rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold px-6" data-testid="ref-submit">Submit Referral</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Contest banner */}
        {contest?.contest?.active && (
          <Card className="bg-gradient-to-r from-[#0F1A0F] to-[#111] border-[#00FF66]/30 p-6 mb-8 relative overflow-hidden" data-testid="contest-banner">
            <div className="absolute -right-6 -top-6 w-40 h-40 rounded-full bg-[#00FF66]/10 blur-3xl" />
            <div className="relative flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#00FF66]/15 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-[#00FF66]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold mb-1">Contest · Live</div>
                  <div className="font-display font-black text-xl md:text-2xl">{contest.contest.name}</div>
                  <div className="text-xs text-white/60 mt-1">
                    Top 3 win {contest.contest.prizes.map((p) => formatINR(p)).join(" · ")} bonus
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold">Your Rank</div>
                  <div className={`font-display font-black text-3xl ${contest.rank && contest.rank <= 3 ? "text-[#00FF66]" : "text-white"}`} data-testid="contest-rank">
                    {contest.rank ? `#${contest.rank}` : "—"}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold">This month</div>
                  <div className="font-display font-black text-xl text-white">{contest.stats.conversions} sales</div>
                </div>
                {contest.prize > 0 && (
                  <div className="border-l border-[#00FF66]/30 pl-6">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#00FF66] font-bold">Prize on line</div>
                    <div className="font-display font-black text-2xl text-[#00FF66]">{formatINR(contest.prize)}</div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Payout reminder */}
        {summary?.available_balance >= 5000 && !payoutReminderDismissed && (
          <Card className="bg-gradient-to-r from-[#00FF66]/10 to-[#00FF66]/5 border-[#00FF66]/40 p-5 mb-8 relative" data-testid="payout-reminder">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#00FF66]/15 flex items-center justify-center flex-shrink-0">
                  <Wallet className="w-5 h-5 text-[#00FF66]" />
                </div>
                <div>
                  <div className="font-display font-bold text-lg text-[#00FF66]">
                    You have {formatINR(summary.available_balance)} ready to withdraw
                  </div>
                  <div className="text-xs text-white/60 mt-0.5">
                    Don't let your cash sit idle. Request a payout — it lands in your bank within 7 days.
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => document.querySelector('[data-testid="tab-payouts"]')?.click()}
                  className="rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold h-10 px-5"
                  data-testid="payout-reminder-cta"
                >
                  Request payout <Send className="w-4 h-4 ml-2" />
                </Button>
                <button
                  onClick={() => { sessionStorage.setItem("payout_reminder_dismissed", "1"); setPayoutReminderDismissed(true); }}
                  className="text-white/40 hover:text-white/70 text-xs px-2"
                  data-testid="payout-reminder-dismiss"
                  aria-label="Dismiss"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </Card>
        )}

        {/* Stats cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          {[
            { icon: <Users className="w-4 h-4" />, label: "Total Referrals", v: summary.total_referrals, testId: "stat-total-refs" },
            { icon: <TrendingUp className="w-4 h-4" />, label: "Conversions", v: summary.stats.purchased, testId: "stat-conversions" },
            { icon: <IndianRupee className="w-4 h-4" />, label: "Total Earned", v: formatINR(summary.total_earned), testId: "stat-earned" },
            { icon: <Wallet className="w-4 h-4" />, label: "Available Balance", v: formatINR(summary.available_balance), testId: "stat-balance", highlight: true },
          ].map((s) => (
            <Card key={s.label} className={`bg-[#111] border-white/10 p-5 ${s.highlight ? "border-[#00FF66]/40" : ""}`} data-testid={s.testId}>
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/50 font-bold">
                <span className={s.highlight ? "text-[#00FF66]" : ""}>{s.icon}</span>{s.label}
              </div>
              <div className={`font-display font-black text-3xl mt-3 ${s.highlight ? "text-[#00FF66]" : "text-white"}`}>{s.v}</div>
            </Card>
          ))}
        </div>

        {/* Referral link card */}
        <Card className="bg-[#111] border-white/10 p-6 lg:p-8 mb-8 relative tracing-border overflow-hidden" data-testid="referral-link-card">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold mb-2">Your referral link</div>
              <div className="font-display font-black text-2xl">Code: <span className="text-[#00FF66]">{user?.referral_code}</span></div>
              <div className="mt-2 text-white/60 text-sm break-all">{referralLink}</div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => copy(referralLink, "Link copied")} className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="copy-link-btn">
                <Copy className="w-4 h-4 mr-2" /> Copy link
              </Button>
              <Button variant="outline" onClick={() => copy(user.referral_code, "Code copied")} className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="copy-code-btn">
                <Copy className="w-4 h-4 mr-2" /> Copy code
              </Button>
              <Button onClick={shareWA} className="rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold" data-testid="share-whatsapp-btn">
                <Share2 className="w-4 h-4 mr-2" /> Share
              </Button>
            </div>
          </div>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="referrals" className="space-y-6">
          <TabsList className="bg-[#111] border border-white/10 p-1 flex-wrap h-auto">
            <TabsTrigger value="referrals" data-testid="tab-referrals" className="data-[state=active]:bg-[#00FF66] data-[state=active]:text-black">Referrals</TabsTrigger>
            <TabsTrigger value="rewards" data-testid="tab-rewards" className="data-[state=active]:bg-[#00FF66] data-[state=active]:text-black">
              Rewards {loyalty?.points_available ? <span className="ml-1.5 text-[10px] font-mono opacity-80">({loyalty.points_available}pts)</span> : null}
            </TabsTrigger>
            <TabsTrigger value="payouts" data-testid="tab-payouts" className="data-[state=active]:bg-[#00FF66] data-[state=active]:text-black">Payouts</TabsTrigger>
            <TabsTrigger value="bank" data-testid="tab-bank" className="data-[state=active]:bg-[#00FF66] data-[state=active]:text-black">Bank / UPI</TabsTrigger>
          </TabsList>

          <TabsContent value="referrals">
            <Card className="bg-[#111] border-white/10 overflow-hidden">
              <div className="grid grid-cols-12 px-6 py-4 text-xs uppercase tracking-[0.2em] text-white/40 font-bold border-b border-white/5">
                <div className="col-span-3">Referee</div>
                <div className="col-span-2">City</div>
                <div className="col-span-3">Model</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2 text-right">Reward</div>
              </div>
              {referrals.length === 0 ? (
                <div className="p-10 text-center text-white/40 text-sm" data-testid="no-referrals">
                  No referrals yet. Hit "Submit new referral" to add your first lead.
                </div>
              ) : referrals.map((r) => (
                <div key={r.id} className="grid grid-cols-12 px-6 py-4 border-b border-white/5 last:border-0 items-center hover:bg-white/[0.02]" data-testid={`referral-row-${r.id}`}>
                  <div className="col-span-3">
                    <div className="font-semibold">{r.referee_name}</div>
                    <div className="text-xs text-white/40">{r.referee_phone}</div>
                  </div>
                  <div className="col-span-2 text-sm text-white/70">{r.city}</div>
                  <div className="col-span-3 text-sm text-white/70">{r.interested_model}</div>
                  <div className="col-span-2">
                    <Badge className={`${STATUS_META[r.status]?.cls} border font-medium`}>{STATUS_META[r.status]?.label}</Badge>
                  </div>
                  <div className="col-span-2 text-right font-display font-bold text-[#00FF66]">
                    {r.reward_amount ? formatINR(r.reward_amount) : "—"}
                  </div>
                </div>
              ))}
            </Card>
          </TabsContent>

          <TabsContent value="rewards">
            {voucher?.current && (
              <Card className="bg-gradient-to-r from-[#00FF66]/[0.08] to-[#111] border-[#00FF66]/40 p-6 mb-6 relative overflow-hidden" data-testid="voucher-card">
                <div className="absolute -right-10 -top-10 w-52 h-52 rounded-full bg-[#00FF66]/10 blur-3xl" />
                <div className="relative flex flex-wrap items-center justify-between gap-5">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-[#00FF66]/15 flex items-center justify-center flex-shrink-0">
                      <Gift className="w-6 h-6 text-[#00FF66]" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold mb-1">This month's voucher</div>
                      <div className="font-display font-black text-2xl md:text-3xl">
                        ₹{voucher.current.discount_inr} off for a friend
                      </div>
                      <div className="text-xs text-white/60 mt-1">
                        Share this code with a friend buying a Rosen — they get ₹{voucher.current.discount_inr} off, you get credited as the referrer.
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="px-4 py-3 rounded-lg border border-[#00FF66]/40 bg-black/40">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold">Voucher code</div>
                      <div className="font-mono font-black text-[#00FF66] text-xl tracking-wider mt-1" data-testid="voucher-code">
                        {voucher.current.code}
                      </div>
                    </div>
                    <Button
                      onClick={() => { navigator.clipboard.writeText(voucher.current.code); toast.success("Code copied"); }}
                      variant="outline"
                      className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white h-11"
                      data-testid="voucher-copy"
                    >
                      <Copy className="w-4 h-4 mr-2" /> Copy
                    </Button>
                    <Button
                      onClick={() => {
                        const msg = `Hey! Here's a ₹${voucher.current.discount_inr} discount voucher for Rosen E-Bikes from ${user.name}: *${voucher.current.code}*. Redeem at ${window.location.origin} · Basavakalyan · 8722915521`;
                        window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
                      }}
                      className="rounded-full bg-[#25D366] text-black hover:bg-[#20BF5B] font-semibold h-11"
                      data-testid="voucher-share-wa"
                    >
                      <Share2 className="w-4 h-4 mr-2" /> Share
                    </Button>
                  </div>
                </div>
                {voucher.current.status === "redeemed" && (
                  <div className="mt-4 pt-4 border-t border-white/10 text-xs text-white/60">
                    ✅ Redeemed by <span className="text-white font-semibold">{voucher.current.redeemed_by_name}</span> ({voucher.current.redeemed_by_phone}) on {new Date(voucher.current.redeemed_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                  </div>
                )}
              </Card>
            )}
            {loyalty && (
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Tier card */}
                <Card className="bg-gradient-to-br from-[#0F1A0F] to-[#111] border-[#00FF66]/30 p-6 lg:col-span-1 relative overflow-hidden" data-testid="loyalty-tier-card">
                  <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-[#00FF66]/10 blur-3xl" />
                  <div className="relative">
                    <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold mb-2 flex items-center gap-2">
                      <Trophy className="w-4 h-4" /> Loyalty Status
                    </div>
                    <div className="font-display font-black text-4xl" style={{ color: loyalty.tier.color }}>
                      {loyalty.tier.name}
                    </div>
                    <div className="mt-6">
                      <div className="text-xs uppercase tracking-wider text-white/50 font-bold">Points Available</div>
                      <div className="font-display font-black text-5xl text-[#00FF66] mt-1" data-testid="loyalty-available">
                        {loyalty.points_available}
                      </div>
                      <div className="text-xs text-white/50 mt-1">Earn {loyalty.per_conversion} pts per conversion</div>
                    </div>
                    {loyalty.next_tier && (
                      <div className="mt-6 pt-4 border-t border-white/10">
                        <div className="flex justify-between text-xs text-white/60 mb-2">
                          <span>Next: <span className="font-bold" style={{ color: loyalty.next_tier.color }}>{loyalty.next_tier.name}</span></span>
                          <span className="text-white/50">{loyalty.next_tier_delta} pts to go</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{
                              width: `${Math.min(100, (loyalty.points_earned / loyalty.next_tier.min) * 100)}%`,
                              backgroundColor: loyalty.next_tier.color,
                            }}
                          />
                        </div>
                      </div>
                    )}
                    <div className="mt-6 flex gap-2 text-[10px] uppercase tracking-[0.15em] text-white/40 font-bold">
                      {loyalty.tiers.map((tt) => (
                        <div
                          key={tt.name}
                          className={`px-2 py-1 rounded ${loyalty.tier.name === tt.name ? "bg-white/10 text-white" : ""}`}
                          style={loyalty.tier.name === tt.name ? { color: tt.color } : {}}
                        >
                          {tt.name}
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>

                {/* Rewards catalog */}
                <Card className="bg-[#111] border-white/10 p-6 lg:col-span-2">
                  <div className="flex items-center gap-2 mb-6">
                    <Gift className="w-4 h-4 text-[#00FF66]" />
                    <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold">Redeem Rewards</div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {loyalty.rewards_catalog.map((r) => {
                      const affordable = loyalty.points_available >= r.cost;
                      return (
                        <div
                          key={r.id}
                          className={`p-4 rounded-lg border ${affordable ? "border-[#00FF66]/30 bg-[#00FF66]/[0.02]" : "border-white/10 bg-white/[0.02]"}`}
                          data-testid={`reward-${r.id}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="font-display font-bold text-base">{r.name}</div>
                              <div className="mt-1 font-mono text-sm text-[#00FF66] font-bold">{r.cost} pts</div>
                            </div>
                            <Award className={`w-5 h-5 ${affordable ? "text-[#00FF66]" : "text-white/20"}`} />
                          </div>
                          <Button
                            onClick={() => redeem(r.id)}
                            disabled={!affordable}
                            className={`w-full mt-3 rounded-full h-9 text-xs font-semibold ${affordable ? "bg-[#00FF66] text-black hover:bg-[#00E55C]" : "bg-white/5 text-white/30 cursor-not-allowed"}`}
                            data-testid={`redeem-${r.id}`}
                          >
                            {affordable ? "Redeem" : `Need ${r.cost - loyalty.points_available} more pts`}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </Card>

                {/* Redemption history */}
                <Card className="bg-[#111] border-white/10 lg:col-span-3 overflow-hidden">
                  <div className="p-6 border-b border-white/5">
                    <div className="text-xs uppercase tracking-[0.3em] text-white/50 font-bold">Redemption History</div>
                  </div>
                  {redemptions.length === 0 ? (
                    <div className="p-10 text-center text-white/40 text-sm" data-testid="no-redemptions">
                      Redeem your first reward to see it here.
                    </div>
                  ) : redemptions.map((r) => (
                    <div key={r.id} className="grid grid-cols-12 px-6 py-4 border-b border-white/5 last:border-0 items-center text-sm" data-testid={`redemption-row-${r.id}`}>
                      <div className="col-span-5 font-medium">{r.reward_name}</div>
                      <div className="col-span-3 font-mono text-[#00FF66]">-{r.cost} pts</div>
                      <div className="col-span-2 text-xs text-white/50">
                        {new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                      </div>
                      <div className="col-span-2 text-right">
                        <Badge className={
                          r.status === "fulfilled" ? "bg-[#00FF66]/10 text-[#00FF66] border-transparent" :
                          r.status === "approved" ? "bg-blue-500/10 text-blue-400 border-transparent" :
                          r.status === "rejected" ? "bg-red-500/10 text-red-400 border-transparent" :
                          "bg-yellow-500/10 text-yellow-400 border-transparent"
                        }>{r.status}</Badge>
                      </div>
                    </div>
                  ))}
                </Card>
              </div>
            )}
          </TabsContent>

          <TabsContent value="payouts">
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="bg-[#111] border-white/10 p-6 md:col-span-1">
                <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold mb-2">Request Payout</div>
                <div className="font-display font-black text-3xl">{formatINR(summary.available_balance)}</div>
                <div className="text-xs text-white/50 mt-1">Available balance</div>
                <div className="mt-6 space-y-3">
                  <Input type="number" placeholder="Enter amount (₹)" value={payoutAmt} onChange={(e) => setPayoutAmt(e.target.value)} className="bg-black/40 border-white/10" data-testid="payout-amount" />
                  <Button onClick={requestPayout} className="w-full rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold" data-testid="request-payout-btn">
                    <Send className="w-4 h-4 mr-2" /> Request Payout
                  </Button>
                </div>
                <div className="mt-4 text-xs text-white/40">
                  Paid out: {formatINR(summary.paid_out)} · Pending: {formatINR(summary.pending_payout)}
                </div>
              </Card>
              <Card className="bg-[#111] border-white/10 md:col-span-2 overflow-hidden">
                <div className="p-6 border-b border-white/5">
                  <div className="text-xs uppercase tracking-[0.3em] text-white/50 font-bold">Payout History</div>
                </div>
                {payouts.length === 0 ? (
                  <div className="p-10 text-center text-white/40 text-sm" data-testid="no-payouts">No payout requests yet.</div>
                ) : payouts.map((p) => (
                  <div key={p.id} className="grid grid-cols-12 px-6 py-4 border-b border-white/5 last:border-0 items-center" data-testid={`payout-row-${p.id}`}>
                    <div className="col-span-4 font-display font-bold text-lg">{formatINR(p.amount)}</div>
                    <div className="col-span-4 text-xs text-white/50">{new Date(p.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
                    <div className="col-span-4 text-right">
                      <Badge className={
                        p.status === "paid" ? "bg-[#00FF66]/10 text-[#00FF66] border-[#00FF66]/30" :
                        p.status === "rejected" ? "bg-red-500/10 text-red-400 border-red-500/20" :
                        "bg-yellow-500/10 text-yellow-400 border-yellow-500/20"
                      }>{p.status.toUpperCase()}</Badge>
                    </div>
                  </div>
                ))}
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="bank">
            <Card className="bg-[#111] border-white/10 p-6 max-w-2xl">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold mb-2">
                <Landmark className="w-4 h-4" /> Bank Details
              </div>
              <div className="font-display font-black text-2xl mb-6">Where should we send your cash?</div>
              <div className="grid gap-4">
                <div>
                  <Label className="text-xs text-white/60 uppercase tracking-[0.2em]">Account holder</Label>
                  <Input value={bank.account_holder} onChange={(e) => setBank({ ...bank, account_holder: e.target.value })} className="mt-2 bg-black/40 border-white/10" data-testid="bank-holder" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-white/60 uppercase tracking-[0.2em]">Account number</Label>
                    <Input value={bank.account_number} onChange={(e) => setBank({ ...bank, account_number: e.target.value })} className="mt-2 bg-black/40 border-white/10" data-testid="bank-account" />
                  </div>
                  <div>
                    <Label className="text-xs text-white/60 uppercase tracking-[0.2em]">IFSC</Label>
                    <Input value={bank.ifsc} onChange={(e) => setBank({ ...bank, ifsc: e.target.value })} className="mt-2 bg-black/40 border-white/10" data-testid="bank-ifsc" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-white/60 uppercase tracking-[0.2em]">UPI ID (optional)</Label>
                  <Input value={bank.upi_id} onChange={(e) => setBank({ ...bank, upi_id: e.target.value })} className="mt-2 bg-black/40 border-white/10" data-testid="bank-upi" placeholder="yourname@upi" />
                </div>
                <Button onClick={saveBank} className="rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold w-fit px-6 mt-2" data-testid="save-bank-btn">
                  Save details
                </Button>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Bulk CSV import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white sm:max-w-lg" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Bulk import referrals</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="p-4 rounded-lg bg-black/30 border border-white/10 space-y-2">
              <div className="text-xs uppercase tracking-[0.2em] text-[#00FF66] font-bold">CSV format</div>
              <div className="font-mono text-xs text-white/70 leading-relaxed">
                referee_name,referee_phone,city,interested_model,notes<br />
                Raju Sharma,9887711223,Bidar,Rosen Q7,Test ride Sunday<br />
                Anita M,9887711224,Kalaburagi,Elite DL,
              </div>
              <a
                href={`data:text/csv;charset=utf-8,${encodeURIComponent("referee_name,referee_phone,city,interested_model,notes\n")}`}
                download="rosen-referrals-template.csv"
                className="inline-flex items-center gap-1 text-xs text-[#00FF66] hover:underline"
                data-testid="download-template"
              >
                <FileDown className="w-3 h-3" /> Download template
              </a>
            </div>
            <label className="block cursor-pointer border-2 border-dashed border-white/15 hover:border-[#00FF66]/40 rounded-lg p-8 text-center transition-colors">
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                data-testid="csv-file-input"
                onChange={(e) => e.target.files?.[0] && uploadCsv(e.target.files[0])}
              />
              <Upload className="w-8 h-8 mx-auto text-white/40 mb-2" />
              <div className="text-sm text-white/70">Click to select a CSV file</div>
              <div className="text-xs text-white/40 mt-1">Only .csv up to 1MB</div>
            </label>
            {importResult && (
              <div className="p-4 rounded-lg bg-[#00FF66]/5 border border-[#00FF66]/20" data-testid="import-result">
                <div className="text-sm">
                  ✅ Imported <span className="font-display font-black text-[#00FF66]">{importResult.inserted}</span> of {importResult.total_rows} rows
                </div>
                {importResult.errors?.length > 0 && (
                  <div className="mt-2 text-xs text-red-300 space-y-0.5">
                    {importResult.errors.slice(0, 5).map((e, i) => (
                      <div key={`imp-err-${e.row ?? i}`}>Row {e.row}: {e.reason}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* QR poster dialog */}
      <Dialog open={posterOpen} onOpenChange={setPosterOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white sm:max-w-md" data-testid="poster-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Your referral poster</DialogTitle>
            <p className="text-xs text-white/50 mt-1">Print & share at exhibitions, dealerships or events.</p>
          </DialogHeader>
          <div className="flex gap-2 -mt-2" role="tablist">
            <button
              onClick={() => setPosterLang("en")}
              className={`px-4 h-8 rounded-full text-xs font-bold uppercase tracking-[0.15em] transition-colors ${posterLang === "en" ? "bg-[#00FF66] text-black" : "bg-white/5 text-white/60 hover:text-white"}`}
              data-testid="poster-lang-en"
            >
              English
            </button>
            <button
              onClick={() => setPosterLang("kn")}
              className={`px-4 h-8 rounded-full text-xs font-bold transition-colors ${posterLang === "kn" ? "bg-[#00FF66] text-black" : "bg-white/5 text-white/60 hover:text-white"}`}
              data-testid="poster-lang-kn"
            >
              ಕನ್ನಡ
            </button>
          </div>
          {posterUrl && (
            <div className="bg-black/30 rounded-lg p-3 border border-white/10">
              <img
                key={posterLang}
                src={posterUrl}
                alt="Referral QR poster"
                className="w-full rounded"
                data-testid="poster-preview"
              />
            </div>
          )}
          <DialogFooter>
            <Button onClick={downloadPoster} className="rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold" data-testid="poster-download-btn">
              <FileDown className="w-4 h-4 mr-2" /> Download PNG
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ChatDrawer />
    </div>
  );
}
